import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Paper, Stack, Typography, LinearProgress } from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { buildPagesFromPdf } from '../utils/pdfUtils.js';
import { showModalDialog } from '../utils/ShowModalDialog.jsx';

export default function UploadStep({ onLoaded }) {
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState('');
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);

  const handleFiles = useCallback(async (file) => {
    setError('');
    setGenerating(true);
    setProgress(0);
    try {
      const buffer = await file.arrayBuffer();

      // Clone for pdf-lib (pristine copy that pdf.js never touches)
      const original = new Uint8Array(buffer.slice(0));

      // Separate copy for pdf.js thumbnails — pass progress callback
      const { pages } = await buildPagesFromPdf(buffer.slice(0), (p) => setProgress(p));

      // Store only the pristine original in state
      onLoaded(original, pages);
    } catch (e) {
      setError('Could not read PDF. Please try another file.');
      await showModalDialog({
              title: 'Error',
              body: `Could not read PDF. Please try another file. Error: ${e.message || e}`,
              type: 'error',
              buttonType: 'ok',
            });
    } finally {
      setGenerating(false);
      setTimeout(() => setProgress(0), 300);
    }
  }, [onLoaded]);

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type === 'application/pdf') handleFiles(file);
  };

  const onInput = (e) => {
    const file = e.target.files?.[0];
    if (file && file.type === 'application/pdf') handleFiles(file);
  };

  useEffect(() => {
    const prevent = (e) => e.preventDefault();
    window.addEventListener('dragover', prevent);
    window.addEventListener('drop', prevent);
    return () => {
      window.removeEventListener('dragover', prevent);
      window.removeEventListener('drop', prevent);
    };
  }, []);

  return (
    <Stack spacing={2}>
      <Paper
        variant="outlined"
        sx={{ p: 4, textAlign: 'center', borderStyle: 'dashed', bgcolor: dragOver ? 'action.hover' : 'transparent' }}
        onDragEnter={() => setDragOver(true)}
        onDragLeave={() => setDragOver(false)}
        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
        onDrop={onDrop}
      >
        <PictureAsPdfIcon sx={{ fontSize: 48, mb: 1 }} />
        <Typography variant="h6" gutterBottom>Drop a PDF here</Typography>
        <Typography variant="body2" color="text.secondary">or</Typography>
        <Box sx={{ mt: 2 }}>
          <Button component="label" variant="contained" disabled={generating}>
            Select PDF
            <input hidden type="file" accept="application/pdf" onChange={onInput} />
          </Button>
        </Box>

        {generating && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress variant="determinate" value={progress} />
            <Typography variant="caption" color="text.secondary">{progress}%</Typography>
          </Box>
        )}

        {error && (
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>{error}</Typography>
        )}
      </Paper>
      <Typography variant="body2" color="text.secondary">
        Everything runs entirely in your browser. No uploads.
      </Typography>
    </Stack>
  );
}
